/**
 * GenUIBlock — renders structured generative UI payloads inline in chat.
 *
 * Five component types, all CSS/SVG-only (no external chart library):
 *   metric_cards      — row of KPI cards
 *   bar_chart         — horizontal bar chart
 *   line_chart        — SVG time-series trend line (single or multi-series)
 *   comparison_table  — styled data table
 *   property_summary  — property snapshot card
 */

import type {
  GenUIPayload,
  MetricCards,
  BarChart,
  LineChart,
  ComparisonTable,
  PropertySummary,
  ReviewSnippets,
} from "@/lib/gen-ui";

export function GenUIBlock({ payload }: { payload: GenUIPayload }) {
  switch (payload.type) {
    case "metric_cards":
      return <MetricCardsBlock data={payload} />;
    case "bar_chart":
      return <BarChartBlock data={payload} />;
    case "line_chart":
      return <LineChartBlock data={payload} />;
    case "comparison_table":
      return <ComparisonTableBlock data={payload} />;
    case "property_summary":
      return <PropertySummaryBlock data={payload} />;
    case "review_snippets":
      return <ReviewSnippetsBlock data={payload} />;
    default:
      return null;
  }
}

// asArray defends against malformed present_data payloads. The validator
// (isValidGenUI) only checks the type discriminator, so an LLM emitting a
// stringified value where an array is expected slips through and crashes
// the renderer mid-stream. Coerce to [] so the block degrades to "no data"
// instead of taking down the whole chat thread.
function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

// ── Metric Cards ──────────────────────────────────────────────────────────────

/**
 * Balanced column count: keeps the last row from looking orphaned.
 *   N ≤ 4       → N cols (1→1, 2→2, 3→3, 4→4)
 *   N = 5, 6    → 3 cols (3+2 / 3+3)
 *   N = 7, 8    → 4 cols (4+3 / 4+4)
 *   N = 9       → 3 cols (3+3+3)
 *   N ≥ 10      → 4 cols
 */
function balancedCols(n: number): number {
  if (n <= 4) return n;
  const rows = Math.ceil(n / 4);
  return Math.ceil(n / rows);
}

function MetricCardsBlock({ data }: { data: MetricCards }) {
  const cards = asArray<MetricCards["cards"][number]>(data.cards);
  if (cards.length === 0) return null;
  const cols = balancedCols(cards.length);
  // On narrow viewports (<480px), cap at 2 cols so a value like "S$1,234,567"
  // doesn't overflow. From sm: up (≥640px in Tailwind default), use the
  // balanced count. Static class names so Tailwind JIT can discover them.
  const smColsClass =
    cols === 1 ? "sm:grid-cols-1"
    : cols === 2 ? "sm:grid-cols-2"
    : cols === 3 ? "sm:grid-cols-3"
    : "sm:grid-cols-4";
  const baseColsClass = cards.length === 1 ? "grid-cols-1" : "grid-cols-2";
  return (
    <div className="my-2 w-full">
      {data.title && (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {data.title}
        </p>
      )}
      <div className={`grid gap-2 ${baseColsClass} ${smColsClass}`}>
        {cards.map((card, i) => (
          <div
            key={i}
            className="flex min-w-0 flex-col rounded-lg border px-3 py-2.5"
            style={{
              borderColor: card.highlight
                ? "rgba(59,130,246,0.35)"
                : "var(--color-border)",
              background: card.highlight
                ? "rgba(59,130,246,0.09)"
                : "var(--color-surface-2)",
            }}
          >
            <span className="truncate text-[10.5px] text-text-muted" title={card.label}>
              {card.label}
            </span>
            <span
              className="mt-0.5 truncate text-[17px] font-bold leading-tight"
              style={{ color: card.highlight ? "#60a5fa" : "var(--color-text)" }}
              title={card.value}
            >
              {card.value}
            </span>
            {card.sub && (
              <span className="mt-0.5 truncate text-[10.5px] text-text-sec" title={card.sub}>
                {card.sub}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bar Chart ─────────────────────────────────────────────────────────────────

function BarChartBlock({ data }: { data: BarChart }) {
  const rows = asArray<BarChart["data"][number]>(data.data).slice(0, 20); // cap at 20 bars to avoid overflow
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map((r) => r.value), 1);
  const fmt = (v: number) =>
    v >= 1_000_000
      ? `${(v / 1_000_000).toFixed(1)}M`
      : v >= 1_000
      ? `${(v / 1_000).toFixed(0)}k`
      : String(v);

  // When many bars, reduce row height & gap so the chart doesn't dominate the screen.
  const dense = rows.length > 10;
  const barHeight = dense ? 16 : 20;
  const rowGap = dense ? "gap-1" : "gap-1.5";
  // Cap the chart's visual height — overflow scrolls vertically.
  const MAX_HEIGHT = 360;
  const estHeight = rows.length * (barHeight + (dense ? 4 : 6));
  const needsScroll = estHeight > MAX_HEIGHT;

  return (
    <div className="my-2 w-full">
      {data.title && (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {data.title}
        </p>
      )}
      {data.y_label && (
        <p className="mb-1.5 text-[11px] text-text-sec">{data.y_label}</p>
      )}
      <div
        className={`flex flex-col ${rowGap} ${needsScroll ? "overflow-y-auto pr-1 scrollbar-thin" : ""}`}
        style={needsScroll ? { maxHeight: MAX_HEIGHT } : undefined}
      >
        {rows.map((row, i) => {
          const pct = (row.value / max) * 100;
          return (
            <div key={i} className="flex items-center gap-2">
              <span
                // Responsive label width: narrow on mobile, wider on desktop.
                // clamp(72px, 22%, 140px) adapts to container width without stealing too much.
                className="shrink-0 truncate text-right text-[11.5px] text-text-sec"
                style={{ width: "clamp(72px, 22%, 140px)" }}
                title={row.label}
              >
                {row.label}
              </span>
              <div
                className="relative min-w-0 flex-1 overflow-hidden rounded-sm"
                style={{ height: barHeight, background: "var(--color-surface-2)" }}
              >
                <div
                  className="absolute left-0 top-0 h-full rounded-sm transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    background: "rgba(59,130,246,0.55)",
                  }}
                />
                <span
                  className="absolute inset-0 flex items-center pl-2 text-[11px] font-medium text-text"
                  style={{ zIndex: 1 }}
                >
                  {fmt(row.value)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {data.x_label && (
        <p className="mt-1.5 text-right text-[10.5px] text-text-muted">{data.x_label}</p>
      )}
    </div>
  );
}

// ── Line Chart ────────────────────────────────────────────────────────────────

const LINE_COLORS = ["#60a5fa", "#a5d16e", "#c084fc", "#fbbf24"];

function LineChartBlock({ data }: { data: LineChart }) {
  type Point = { label: string; value: number };
  type Series = { name?: string; data: Point[] };
  // Coerce both series and data to arrays — either field may arrive
  // mistyped from the LLM (see asArray comment above).
  const rawSeries = Array.isArray(data.series) ? data.series : null;
  const series: Series[] = rawSeries
    ? rawSeries.map((s): Series => ({ name: s?.name, data: asArray<Point>(s?.data) }))
    : [{ data: asArray<Point>(data.data) }];

  const allValues = series.flatMap((s) => s.data.map((r) => r.value));
  const allLabels = series[0]?.data.map((r) => r.label) ?? [];
  const n = allLabels.length;
  if (n === 0) return null;

  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const range = maxVal - minVal || 1;

  const W = 480;
  const H = 150;
  const PAD_L = 46;
  const PAD_R = 12;
  const PAD_T = 12;
  const PAD_B = 28;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const toX = (i: number) => PAD_L + (n > 1 ? (i / (n - 1)) * innerW : innerW / 2);
  const toY = (v: number) => PAD_T + innerH - ((v - minVal) / range) * innerH;

  const fmt = (v: number) =>
    v >= 1_000_000
      ? `${(v / 1_000_000).toFixed(1)}M`
      : v >= 1_000
      ? `${(v / 1_000).toFixed(0)}k`
      : String(v);

  const yTicks = [minVal, minVal + range / 2, maxVal];
  const labelStep = Math.max(1, Math.ceil(n / 8));
  const isSingle = series.length === 1;
  const areaColor = LINE_COLORS[0];

  return (
    <div className="my-2 w-full">
      {data.title && (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {data.title}
        </p>
      )}
      {data.y_label && (
        <p className="mb-1 text-[11px] text-text-sec">{data.y_label}</p>
      )}
      <div className="w-full">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{ width: "100%", height: "auto" }}
          aria-hidden="true"
        >
          <defs>
            {isSingle && (
              <linearGradient id="lc-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={areaColor} stopOpacity={0.18} />
                <stop offset="100%" stopColor={areaColor} stopOpacity={0} />
              </linearGradient>
            )}
          </defs>

          {/* Horizontal grid lines */}
          {yTicks.map((v, i) => {
            const y = toY(v);
            return (
              <g key={i}>
                <line
                  x1={PAD_L} y1={y} x2={W - PAD_R} y2={y}
                  stroke="rgba(255,255,255,0.07)"
                  strokeWidth={1}
                />
                <text
                  x={PAD_L - 4} y={y + 3.5}
                  textAnchor="end"
                  fontSize={9}
                  fill="rgba(255,255,255,0.35)"
                >
                  {fmt(v)}
                </text>
              </g>
            );
          })}

          {/* X-axis labels */}
          {allLabels.map((label, i) => {
            if (i % labelStep !== 0 && i !== n - 1) return null;
            return (
              <text
                key={i}
                x={toX(i)}
                y={H - 5}
                textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
                fontSize={9}
                fill="rgba(255,255,255,0.35)"
              >
                {label}
              </text>
            );
          })}

          {/* Area fill for single series */}
          {isSingle && series[0].data.length > 1 && (
            <path
              d={[
                `M ${toX(0)},${toY(series[0].data[0].value)}`,
                ...series[0].data.slice(1).map((r, i) => `L ${toX(i + 1)},${toY(r.value)}`),
                `L ${toX(n - 1)},${PAD_T + innerH}`,
                `L ${toX(0)},${PAD_T + innerH}`,
                "Z",
              ].join(" ")}
              fill="url(#lc-fill)"
            />
          )}

          {/* Series lines + dots */}
          {series.map((s, si) => {
            const color = LINE_COLORS[si % LINE_COLORS.length];
            const pts = s.data.map((r, i) => `${toX(i)},${toY(r.value)}`).join(" ");
            return (
              <g key={si}>
                <polyline
                  points={pts}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {s.data.map((r, i) => (
                  <circle key={i} cx={toX(i)} cy={toY(r.value)} r={3} fill={color} />
                ))}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend for multi-series */}
      {!isSingle && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
          {series.map((s, si) => (
            <div key={si} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-4 rounded-sm"
                style={{ background: LINE_COLORS[si % LINE_COLORS.length] }}
              />
              <span className="text-[10.5px] text-text-sec">{s.name}</span>
            </div>
          ))}
        </div>
      )}

      {data.x_label && (
        <p className="mt-1 text-right text-[10.5px] text-text-muted">{data.x_label}</p>
      )}
    </div>
  );
}

// ── Comparison Table ──────────────────────────────────────────────────────────

function ComparisonTableBlock({ data }: { data: ComparisonTable }) {
  const columns = asArray<string>(data.columns);
  const rows = asArray<unknown>(data.rows).filter((r): r is string[] => Array.isArray(r));
  if (columns.length === 0) return null;
  return (
    <div className="my-2 w-full overflow-x-auto">
      {data.title && (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {data.title}
        </p>
      )}
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={i}
                className="whitespace-nowrap border-b border-t px-2.5 py-1.5 text-left font-semibold text-text-sec"
                style={{ borderColor: "var(--color-border)" }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className="transition-colors"
              style={{
                background: ri % 2 === 0 ? "transparent" : "var(--color-surface-2)",
              }}
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="whitespace-nowrap px-2.5 py-1.5 text-text"
                  style={{
                    borderBottom: "1px solid var(--color-border-subtle)",
                    fontWeight: ci === 0 ? 500 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Review Snippets ───────────────────────────────────────────────────────────

function ReviewSnippetsBlock({ data }: { data: ReviewSnippets }) {
  const cards = asArray<ReviewSnippets["cards"][number]>(data.cards);
  if (cards.length === 0) return null;
  return (
    <div className="my-2 w-full">
      {data.title && (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {data.title}
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {cards.map((c, i) => {
          // Reddit content lands here via either the direct OAuth client
          // ("reddit") or the PullPush community mirror ("pullpush"). They
          // render identically — same source, same accent.
          const isReddit = c.backend === "reddit" || c.backend === "pullpush";
          const sourceLabel = isReddit && c.subreddit ? `r/${c.subreddit}` : c.source;
          const accent = isReddit ? "#ff7a59" : "#60a5fa";
          return (
            <li
              key={i}
              className="rounded-lg border px-3 py-2.5"
              style={{
                borderColor: "var(--color-border)",
                background: "var(--color-surface-2)",
              }}
            >
              <div className="flex items-start gap-2">
                <span
                  className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: accent }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block break-words text-[13px] font-semibold leading-snug text-text hover:underline"
                    title={c.url}
                  >
                    {c.title || c.url}
                  </a>
                  {c.snippet && (
                    <p className="mt-1 break-words text-[12px] leading-snug text-text-sec">
                      {c.snippet}
                    </p>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10.5px] text-text-muted">
                    <span style={{ color: accent }}>{sourceLabel}</span>
                    {typeof c.num_comments === "number" && (
                      <span>· {c.num_comments} comments</span>
                    )}
                    {typeof c.score === "number" && c.score > 0 && (
                      <span>· {Math.round(c.score)} points</span>
                    )}
                    {c.published_at && (
                      <span>· {c.published_at.slice(0, 10)}</span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Property Summary ──────────────────────────────────────────────────────────

function PropertySummaryBlock({ data }: { data: PropertySummary }) {
  return (
    <div
      className="my-2 w-full rounded-xl border p-4"
      style={{
        borderColor: "rgba(59,130,246,0.22)",
        background:
          "linear-gradient(135deg, rgba(59,130,246,0.07) 0%, rgba(139,92,246,0.05) 100%)",
      }}
    >
      {/* Header row — stacks on narrow, side-by-side from 420px up */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="break-words text-[14px] font-semibold leading-tight text-text">
            {data.address}
          </p>
          <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11.5px] text-text-sec">
            {data.town && <span>{data.town}</span>}
            {data.flat_type && <span>·&nbsp;{data.flat_type}</span>}
            {data.floor_area && <span>·&nbsp;{data.floor_area}</span>}
          </div>
        </div>
        <div className="shrink-0 sm:text-right">
          <p className="text-[16px] font-bold text-[#60a5fa]">{data.price}</p>
          {data.psf && (
            <p className="text-[11px] text-text-muted">{data.psf}</p>
          )}
        </div>
      </div>

      {/* Lease badge */}
      {data.lease && (
        <div className="mt-2">
          <span
            className="inline-block rounded-full px-2.5 py-0.5 text-[11px]"
            style={{
              background: "rgba(165,209,110,0.12)",
              color: "#a5d16e",
              border: "1px solid rgba(165,209,110,0.25)",
            }}
          >
            {data.lease}
          </span>
        </div>
      )}

      {/* Extra metrics */}
      {Array.isArray(data.metrics) && data.metrics.length > 0 && (
        <div
          className="mt-3 grid gap-x-4 gap-y-1.5 border-t pt-2.5"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            borderColor: "var(--color-border)",
          }}
        >
          {data.metrics.map((m, i) => (
            <div key={i}>
              <p className="text-[10.5px] text-text-muted">{m.label}</p>
              <p className="text-[12.5px] font-medium text-text">{m.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
